import React, { useState } from "react";
import {
  ChatCompletionRequest,
  ChatMessage,
  ToolCall,
} from "../../model_gen/chat";
import { CheckCircleTwoTone, CloseCircleTwoTone } from "@ant-design/icons";
import { Button, message, Spin } from "antd";
import { TestSetService } from "../../services/TestSetService";

interface RequestViewProps {
  request: ChatCompletionRequest | null | undefined;
  apiKey: string;
  isEditable?: boolean;
  testSetId?: string;
  testSetRequestId?: string | null;
  onUpdated?: (updatedRequest: ChatCompletionRequest) => void;
  systemPrompt?: string;
}

const RequestView: React.FC<RequestViewProps> = ({
  request,
  apiKey,
  isEditable = false,
  testSetId,
  testSetRequestId,
  onUpdated,
  systemPrompt,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [editedContent, setEditedContent] = useState<string | null>(null);
  const [pendingRequest, setPendingRequest] =
    useState<ChatCompletionRequest | null>(null);
  const [editedToolCalls, setEditedToolCalls] = useState<ToolCall[] | null>(
    null
  );
  const [showAddToolCall, setShowAddToolCall] = useState(false);
  const [newToolCall, setNewToolCall] = useState({ name: "", arguments: "" });

  if (!request) {
    return <div>No request found</div>;
  }

  if (!request.params?.messages?.length) {
    return <div>Request messages empty</div>;
  }

  let parsedMetadata = null;
  try {
    parsedMetadata = request.metadata?._maitai_request_meta
      ? JSON.parse(request.metadata._maitai_request_meta as string)
      : null;
  } catch (e) {
    console.error("Failed to parse metadata:", e);
  }

  const getMessageContent = (msg: ChatMessage, index: number) => {
    const isLastMessage = index === request.params.messages.length - 1;
    if (isLastMessage) {
      if (pendingRequest) {
        return editedContent;
      }
      const testResponse = request.metadata?._maitai_test_request_response;
      return testResponse !== undefined && testResponse !== null
        ? testResponse
        : msg.content;
    }
    return msg.content;
  };

  const handleContentChange = (index: number, newContent: string) => {
    if (!request || !isEditable) return;

    const updatedRequest = JSON.parse(JSON.stringify(request));
    setEditedContent(newContent);
    setPendingRequest(updatedRequest);
  };

  const handleToolCallChange = (
    toolCallIndex: number,
    field: string,
    value: string
  ) => {
    if (!request || !isEditable || editedToolCalls === null) return;

    // Ensure pendingRequest is set so the save button appears
    if (!pendingRequest) {
      const updatedRequest = JSON.parse(JSON.stringify(request));
      setPendingRequest(updatedRequest);
    }

    const updatedToolCalls = [...editedToolCalls];
    if (field === "name") {
      updatedToolCalls[toolCallIndex].function.name = value;
    } else if (field === "arguments") {
      updatedToolCalls[toolCallIndex].function.arguments = value;
    }

    setEditedToolCalls(updatedToolCalls);
  };

  const handleAddToolCall = () => {
    if (!editedToolCalls) {
      // Initialize from current message if not already done
      const lastAssistantMessage = request.params.messages
        .filter((m) => m.role === "assistant")
        .pop();

      const currentToolCalls = lastAssistantMessage?.tool_calls || [];
      setEditedToolCalls([...currentToolCalls]);
    }

    setShowAddToolCall(true);
  };

  const confirmAddToolCall = () => {
    if (!editedToolCalls) return;

    const newToolCallObj = {
      id: `call_${Date.now()}`,
      type: "function",
      function: {
        name: newToolCall.name,
        arguments: newToolCall.arguments,
      },
    };

    setEditedToolCalls([...editedToolCalls, newToolCallObj]);
    setNewToolCall({ name: "", arguments: "" });
    setShowAddToolCall(false);
  };

  const cancelAddToolCall = () => {
    setNewToolCall({ name: "", arguments: "" });
    setShowAddToolCall(false);
  };

  const handleSave = () => {
    if (!pendingRequest) return;

    // Create updated request with edited content and tool calls
    const updatedRequest = JSON.parse(JSON.stringify(pendingRequest));

    // Find the last assistant message
    const lastAssistantIndex = updatedRequest.params.messages
      .map((m: ChatMessage, i: number) => ({ role: m.role, index: i }))
      .filter((m: any) => m.role === "assistant")
      .pop()?.index;

    if (lastAssistantIndex !== undefined) {
      // Update content if edited
      if (editedContent !== null) {
        updatedRequest.metadata._maitai_test_request_response = editedContent;
      }

      // Update tool calls if edited
      if (editedToolCalls !== null) {
        updatedRequest.metadata._maitai_test_request_response_tool_calls =
          editedToolCalls;
      }
    }

    if (!testSetRequestId) {
      // This request hasn't been added to the test set yet
      onUpdated?.(updatedRequest);
      setEditedContent(null);
      setEditedToolCalls(null);
      setPendingRequest(null);
      return;
    }

    setIsLoading(true);

    TestSetService.updateTestSetRequestResponse(
      testSetId,
      updatedRequest.request_id,
      {
        test_set_request_id: testSetRequestId,
        message_content: editedContent,
        tool_calls: editedToolCalls,
      },
      setIsLoading,
      apiKey
    ).subscribe(
      () => {
        if (onUpdated) {
          onUpdated(updatedRequest);
        }
        setEditedContent(null);
        setEditedToolCalls(null);
        setPendingRequest(null);
        message.success("Message updated successfully");
      },
      (error: any) => {
        console.error("Failed to update message:", error);
        message.error("Failed to update message");
      }
    );
  };

  const handleCancel = () => {
    setEditedContent(null);
    setEditedToolCalls(null);
    setPendingRequest(null);
    setShowAddToolCall(false);
  };

  const isMessageEditable = (msg: ChatMessage, index: number) => {
    if (!isEditable) return false;

    // Find the last assistant message
    const lastAssistantIndex = request.params.messages
      .map((m, i) => ({ role: m.role, index: i }))
      .filter((m) => m.role === "assistant")
      .pop()?.index;

    return index === lastAssistantIndex;
  };

  const isMessageEdited = (msg: ChatMessage, index: number) => {
    const isLastMessage = index === request.params.messages.length - 1;
    return (
      isLastMessage &&
      (request.metadata?._maitai_test_request_response !== undefined ||
        (request.metadata?._maitai_test_request_response_tool_calls &&
          (
            request.metadata
              ?._maitai_test_request_response_tool_calls as ToolCall[]
          ).length > 0))
    );
  };

  const getToolCalls = (msg: ChatMessage) => {
    if (
      editedToolCalls !== null &&
      isMessageEditable(msg, request.params.messages.indexOf(msg))
    ) {
      return editedToolCalls;
    }
    if (request.metadata?._maitai_test_request_response_tool_calls) {
      return request.metadata
        ._maitai_test_request_response_tool_calls as ToolCall[];
    }
    return msg.tool_calls || [];
  };

  const renderMessageContent = (message: ChatMessage) => {
    if (message.role === "system" && systemPrompt) {
      return systemPrompt;
    }
    let content = message.content ?? "";

    // Only add tool calls to content for non-editable messages
    if (
      message.tool_calls &&
      !isMessageEditable(message, request.params.messages.indexOf(message))
    ) {
      if (content) {
        content += "\n\n";
      }
      content += "Tool calls:";
      for (const toolCall of message.tool_calls) {
        content += `\n<function=${toolCall.function.name}>\n${toolCall.function.arguments}\n</function>`;
      }
    }

    return content;
  };

  return (
    <Spin spinning={isLoading}>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ width: "600px", maxWidth: "600px", boxSizing: "border-box" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "calc(100% - 24px)",
          }}>
          <strong>Request</strong>
          {isEditable && pendingRequest && (
            <div style={{ display: "flex", gap: "8px" }}>
              <Button
                icon={<CloseCircleTwoTone twoToneColor="#eb4a3f" />}
                onClick={handleCancel}
              />
              <Button
                icon={<CheckCircleTwoTone twoToneColor="#52c41a" />}
                onClick={handleSave}
              />
            </div>
          )}
        </div>
        {parsedMetadata?.summary && (
          <div
            style={{
              margin: "8px 0",
              padding: "8px",
            }}>
            {parsedMetadata.summary}
          </div>
        )}
        {request.params.messages.map((msg: ChatMessage, index: number) => (
          <details
            key={index}
            style={{ marginTop: "8px", width: "100%" }}
            onClick={(e) => e.stopPropagation()}
            open={index === request.params.messages.length - 1}>
            <summary
              style={{
                fontWeight: "500",
                cursor: "pointer",
                padding: "4px 0",
              }}>
              {msg.role}
              {msg.role === "system" &&
                systemPrompt &&
                msg.content !== systemPrompt &&
                "*"}
              {isMessageEdited(msg, index) && "*"}
            </summary>
            {isMessageEditable(msg, index) ? (
              <>
                <textarea
                  value={getMessageContent(msg, index) as string}
                  onChange={(e) => handleContentChange(index, e.target.value)}
                  style={{
                    width: "100%",
                    maxWidth: "calc(100% - 32px)",
                    padding: "4px 8px",
                    border: "1px solid #d9d9d9",
                    borderRadius: "4px",
                    resize: "vertical",
                    boxSizing: "border-box",
                    fontFamily: "inherit",
                    fontSize: "inherit",
                    lineHeight: "inherit",
                    overflowY: "auto",
                    minHeight: getMessageContent(msg, index) ? "100px" : "20px",
                    maxHeight: getMessageContent(msg, index)
                      ? "300px"
                      : "100px",
                    margin: "0 8px",
                  }}
                />

                {/* Tool Calls Section - Only shown for editable messages */}
                <div style={{ margin: "8px" }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "4px",
                    }}>
                    <strong>Tool Calls</strong>
                    <Button
                      type="primary"
                      size="small"
                      style={{ marginRight: "24px" }}
                      onClick={() => {
                        if (!pendingRequest) {
                          const updatedRequest = JSON.parse(
                            JSON.stringify(request)
                          );
                          setPendingRequest(updatedRequest);
                        }
                        handleAddToolCall();
                      }}>
                      Add Tool Call
                    </Button>
                  </div>

                  {getToolCalls(msg).map((toolCall: any, toolIndex: number) => (
                    <div
                      key={toolCall.id || toolIndex}
                      style={{
                        borderBottom: "1px solid #d9d9d9",
                        padding: "4px 0",
                        marginBottom: "4px",
                      }}>
                      <div style={{ marginBottom: "2px" }}>
                        <strong style={{ fontSize: "0.85em" }}>
                          Function:
                        </strong>
                        <input
                          value={toolCall.function.name}
                          onChange={(e) => {
                            // Initialize editedToolCalls if not already done
                            if (editedToolCalls === null) {
                              setEditedToolCalls(getToolCalls(msg));
                            }
                            handleToolCallChange(
                              toolIndex,
                              "name",
                              e.target.value
                            );
                          }}
                          style={{
                            width: "100%",
                            maxWidth: "calc(100% - 32px)",
                            padding: "2px 4px",
                            border: "1px solid #d9d9d9",
                            borderRadius: "2px",
                            marginTop: "1px",
                            fontSize: "0.85em",
                            height: "24px",
                          }}
                        />
                      </div>
                      <div>
                        <strong style={{ fontSize: "0.85em" }}>
                          Arguments:
                        </strong>
                        <textarea
                          value={toolCall.function.arguments}
                          onChange={(e) => {
                            // Initialize editedToolCalls if not already done
                            if (editedToolCalls === null) {
                              setEditedToolCalls(getToolCalls(msg));
                            }
                            handleToolCallChange(
                              toolIndex,
                              "arguments",
                              e.target.value
                            );
                          }}
                          style={{
                            width: "100%",
                            maxWidth: "calc(100% - 32px)",
                            padding: "2px 4px",
                            border: "1px solid #d9d9d9",
                            borderRadius: "2px",
                            resize: "vertical",
                            maxHeight: "100px",
                            marginTop: "1px",
                            fontSize: "0.85em",
                            lineHeight: "1.2",
                          }}
                        />
                      </div>
                    </div>
                  ))}

                  {/* Add New Tool Call Form */}
                  {showAddToolCall && (
                    <div
                      style={{
                        borderBottom: "1px solid #1890ff",
                        padding: "4px 0",
                        marginBottom: "4px",
                      }}>
                      <div style={{ marginBottom: "2px" }}>
                        <strong style={{ fontSize: "0.85em" }}>
                          Function:
                        </strong>
                        <input
                          value={newToolCall.name}
                          onChange={(e) =>
                            setNewToolCall({
                              ...newToolCall,
                              name: e.target.value,
                            })
                          }
                          style={{
                            width: "100%",
                            maxWidth: "calc(100% - 32px)",
                            padding: "2px 4px",
                            border: "1px solid #d9d9d9",
                            borderRadius: "2px",
                            marginTop: "1px",
                            fontSize: "0.85em",
                            height: "24px",
                          }}
                        />
                      </div>
                      <div style={{ marginBottom: "2px" }}>
                        <strong style={{ fontSize: "0.85em" }}>
                          Arguments:
                        </strong>
                        <textarea
                          value={newToolCall.arguments}
                          onChange={(e) =>
                            setNewToolCall({
                              ...newToolCall,
                              arguments: e.target.value,
                            })
                          }
                          style={{
                            width: "100%",
                            maxWidth: "calc(100% - 32px)",
                            padding: "2px 4px",
                            border: "1px solid #d9d9d9",
                            borderRadius: "2px",
                            resize: "vertical",
                            minHeight: "40px",
                            maxHeight: "100px",
                            marginTop: "1px",
                            fontSize: "0.85em",
                            lineHeight: "1.2",
                          }}
                        />
                      </div>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "flex-end",
                          gap: "4px",
                          marginTop: "2px",
                        }}>
                        <Button size="small" onClick={cancelAddToolCall}>
                          Cancel
                        </Button>
                        <Button
                          type="primary"
                          size="small"
                          onClick={confirmAddToolCall}
                          disabled={!newToolCall.name.trim()}>
                          Add
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div
                style={{
                  padding: "4px 8px",
                  whiteSpace: "pre-wrap",
                  maxHeight: "300px",
                  overflowY: "auto",
                  wordBreak: "break-word",
                  width: "100%",
                  maxWidth: "584px",
                  boxSizing: "border-box",
                }}>
                {renderMessageContent(msg)}
              </div>
            )}
          </details>
        ))}
      </div>
    </Spin>
  );
};

export default RequestView;
